import { useCallback } from 'react';
import { EditorState, ContentState, ContentBlock } from 'draft-js';
import { isCursorInsideAutocomplete } from '../AutoComplete/utils/autocompleteUtils';
import { filterSuggestions } from '../SuggestionsDropdown/utils/suggestionUtils';

/**
 * Props for useEditorChange hook
 */
interface UseEditorChangeProps {
  setEditorState: (state: EditorState) => void;
  setDropdownVisible: (visible: boolean) => void;
  setFilteredSuggestions: (suggestions: readonly string[]) => void;
  setSelectedIndex: (index: number) => void;
  dropdownVisible: boolean;
  filteredSuggestions: readonly string[];
  selectedIndex: number;
  allSuggestions: string[];
}

/**
 * Hook to handle editor content changes and manage suggestion dropdown.
 * Handles showing/hiding dropdown, filtering suggestions, and maintaining selection.
 *
 * @param UseEditorChangeProps - Configuration and state setters for editor and suggestions
 * @returns Callback function to handle editor state changes
 */
export const useEditorChange = ({
  setEditorState,
  setDropdownVisible,
  setFilteredSuggestions,
  setSelectedIndex,
  dropdownVisible,
  filteredSuggestions,
  selectedIndex,
  allSuggestions
}: UseEditorChangeProps) => {
  return useCallback((newState: EditorState): void => {
    setEditorState(newState);
   
    // Get current editor content and cursor position
    const currentContent: ContentState = newState.getCurrentContent();
    const currentBlock: ContentBlock = currentContent.getBlockForKey(newState.getSelection().getStartKey());
    const text: string = currentBlock.getText();
    const cursorPosition: number = newState.getSelection().getStartOffset();
    const lastTwoChars = text.slice(Math.max(0, cursorPosition - 2), cursorPosition);

    // Check if the last character is '<>'
    if (lastTwoChars === '<>' && !dropdownVisible) {
      setDropdownVisible(true);
      setFilteredSuggestions(allSuggestions); // Show all suggestions initially
    }
   
    if (dropdownVisible) {
      // Get text before cursor and find the relevant '<>'
      const textBeforeCursor: string = text.slice(0, cursorPosition);
      const lastTriggerIndex: number = textBeforeCursor.lastIndexOf('<>');
     
      // Update suggestions if cursor is in valid position
      if (!isCursorInsideAutocomplete(newState) && lastTriggerIndex !== -1) {
        // Get input text from after the '<>' up to the cursor
        const input: string = text.slice(lastTriggerIndex + 2, cursorPosition);
        const result = filterSuggestions(input, allSuggestions, [...filteredSuggestions], selectedIndex);
        setFilteredSuggestions(result.filteredSuggestions);
        setSelectedIndex(result.selectedIndex);
      } else {
        // Hide dropdown if cursor moves to invalid position
        setDropdownVisible(false);
        setSelectedIndex(0);
      }
    }
  }, [
    dropdownVisible,
    filteredSuggestions,
    selectedIndex,
    allSuggestions,
    setEditorState,
    setDropdownVisible,
    setFilteredSuggestions,
    setSelectedIndex
  ]);
};